import './tabazienda.css';
import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Spinner,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { BsTrash, BsInfoCircle, BsPlusCircle } from "react-icons/bs";
import { ServiceFactory } from "../../Api/Services/ServiceFactory";
import { ServiceNames } from "../../Api/registry/Services";
import { useToast } from "../ToastContext/ToastContext";
import { useSelector, useDispatch } from 'react-redux';
import { fetchPerson } from '../../store/slices/personSlice';
import FarmDetailSection from "./FarmDetailSection";
import { useAppKeycloak } from '../../keycloak/keycloakUtils';

const FarmManagement = () => {
  const { t } = useTranslation(["usersetting"]);
  const { showToast } = useToast();
  const [userService, setUserService] = useState(null);
  const loading = useSelector((state) => state.person?.loading);
  const { keycloak } = useAppKeycloak();
  const [editingFarm, setEditingFarm] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    const initializeService = async () => {
      const service = await ServiceFactory.getService(ServiceNames.UserService);
      setUserService(service);
    }
    initializeService();
  }, []);

  // Otteniamo le farms dal PersonDto nello store
  const personData = useSelector((state) => state.person?.person);
  const farms = personData?.agriFarms || [];
  const userId = keycloak.tokenParsed?.sub;

  const handleEditFarm = (farm) => {
    setEditingFarm(farm);
  };

  const handleAddFarm = () => {
    setEditingFarm({
      id: null,
      name: "",
      alternatename: "",
      description: "",
      ownedby: userId,
      buildings: [],
      parcels: [],
    });
  };

  const handleCancelEdit = () => {
    setEditingFarm(null);
  };

  const handleDeleteFarm = async (farmId) => {
    if (window.confirm(t("usersetting:messages.confirmdeletefarm"))) {
      try {
        await userService.deleteFarm(farmId);
        showToast(t("common:messages.success"), "success");
        await dispatch(fetchPerson());
      } catch (error) {
        showToast(error.message || t("error:wrong"), "error");
      }
    }
  };

  const handleSaveFarm = async (farmData) => {
    try {
      if (!farmData.id) {
        await userService.addFarm(farmData, userId);
        showToast(t("common:messages.success"), "success");
        await dispatch(fetchPerson());
      } else {
        await userService.updateFarm(farmData);
        await dispatch(fetchPerson());
        showToast(t("common:messages.success"), "success");
      }
      setEditingFarm(null);
    } catch (error) {
      showToast(error.message || t("error:wrong"), "error");
    }
  };

  return (
    <Container fluid>
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-end mb-2">
            <Button variant="success" onClick={handleAddFarm}>
              <BsPlusCircle className="me-1" />{" "}
              {t("usersetting:button.caption.addfarm")}
            </Button>
          </div>
          <div style={{ overflowY: "auto" }}>
            <Table striped bordered hover responsive className="my-farm-table" style={{ marginBottom: '0px' }}>
              <thead>
                <tr>
                  <th>{t("usersetting:caption.tablefarmname")}</th>
                  <th>{t("usersetting:caption.tablefarmalternatename")}</th>
                  <th>{t("usersetting:caption.tableoperations")}</th>
                </tr>
              </thead>
              <tbody style={{ maxHeight: "20px", overflowY: "auto" }}>
                {loading || !personData ? (
                  <tr>
                    <td colSpan={3}>
                      <Spinner
                        animation="border"
                        role="status"
                        className="d-flex m-auto"
                      >
                        <span className="visually-hidden">{t("usersetting:messages.loading")}</span>
                      </Spinner>
                    </td>
                  </tr>
                ) : farms.length > 0 ? (
                  farms.map((farm) => (
                    <tr key={farm.id}>
                      <td>{farm.name}</td>
                      <td>{farm.alternateName}</td>
                      <td>
                        <Button
                          variant="link"
                          onClick={() => handleEditFarm(farm)}
                          title={t("usersetting:tooltip.farmdetail")}
                        >
                          <BsInfoCircle />
                        </Button>
                        <Button
                          variant="link"
                          onClick={() => handleDeleteFarm(farm.id)}
                          title={t("usersetting:tooltip.delete")}
                        >
                          <BsTrash />
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3}>
                      {t("common:messages.noitemfound")}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
          {editingFarm && (
            <>
              <FarmDetailSection
                farm={editingFarm}
                onSave={handleSaveFarm}
                onCancel={handleCancelEdit}
                isNewFarm={!editingFarm.id}
              />
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default FarmManagement;